import { DialogComponent } from '@theme/dialog';
import { a as cookieApi } from './shared-import-js.cookie.bundle.js';

if (!customElements.get('timed-popup-dialog')) {
  class TimedPopupDialog extends DialogComponent {
    connectedCallback() {
      super.connectedCallback();

      this.#showSuccessToastIfNeeded();

      if (!this.elements.dialog) return;

      this.#closeTriggers = Array.from(this.querySelectorAll('[dialog-close]'));
      this.#closeTriggers.forEach((trigger) => {
        trigger.addEventListener('click', this.#handleCloseTriggerClick);
      });

      this.elements.dialog.addEventListener('close', this.#handleDialogClose);

      this.querySelector('form')?.addEventListener('submit', this.#handleFormSubmit);

      if (this.isDesignMode) {
        this.#bindDesignModeEvents();
        this.#handleInitialDesignModeState();
        return;
      }

      if (this.#isDismissed()) return;

      this.#openTimeout = window.setTimeout(() => {
        if (!this.#isDismissed()) {
          this.open();
        }
      }, this.delayMs);
    }

    disconnectedCallback() {
      super.disconnectedCallback();

      if (this.#openTimeout) {
        window.clearTimeout(this.#openTimeout);
      }

      this.#closeTriggers.forEach((trigger) => {
        trigger.removeEventListener('click', this.#handleCloseTriggerClick);
      });
      this.#closeTriggers = [];

      this.elements.dialog?.removeEventListener('close', this.#handleDialogClose);
      this.querySelector('form')?.removeEventListener('submit', this.#handleFormSubmit);
      document.removeEventListener('shopify:section:select', this.#handleSectionSelect);
      document.removeEventListener('shopify:section:deselect', this.#handleSectionDeselect);
    }

    #openTimeout;
    #closeTriggers = [];

    #handleCloseTriggerClick = (event) => {
      event.preventDefault();
      this.close();
    };

    #bindDesignModeEvents() {
      document.addEventListener('shopify:section:select', this.#handleSectionSelect);
      document.addEventListener('shopify:section:deselect', this.#handleSectionDeselect);
    }

    #handleInitialDesignModeState() {
      if (window.theme?.designMode?.selected === this.sectionId) {
        this.open();
      }
    }

    #handleSectionSelect = (event) => {
      if (!this.#matchesSection(event.target)) return;

      this.open();
    };

    #handleSectionDeselect = (event) => {
      if (!this.#matchesSection(event.target)) return;

      this.close();
    };

    #matchesSection(sectionElement) {
      if (!(sectionElement instanceof Element)) return false;

      if (this.sectionId) {
        return sectionElement.id === `shopify-section-${this.sectionId}`;
      }

      return sectionElement.contains(this);
    }

    #handleDialogClose = () => {
      this.#setDismissedCookie();
    };

    #handleFormSubmit = () => {
      this.#setDismissedCookie();
      this.#setToastSuccessCookie();
    };

    #showSuccessToastIfNeeded() {
      if (!this.#consumeToastSuccessCookie()) return;

      const successMessage = this.successMessage;

      if (!successMessage) return;

      this.#showToast(successMessage);
    }

    #setToastSuccessCookie() {
      cookieApi.set(this.toastCookieName, 'shown', {
        path: '/',
        expires: this.cookieExpiresDays,
      });
    }

    #consumeToastSuccessCookie() {
      const shouldShowToast = cookieApi.get(this.toastCookieName) === 'shown';

      if (!shouldShowToast) return false;

      cookieApi.remove(this.toastCookieName, { path: '/' });

      return true;
    }

    #showToast(message) {
      const toast = document.createElement('div');
      toast.className = 'timed-popup-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      toast.textContent = message;

      document.body.appendChild(toast);

      window.requestAnimationFrame(() => {
        toast.classList.add('is-visible');
      });

      window.setTimeout(() => {
        toast.classList.remove('is-visible');

        window.setTimeout(() => {
          toast.remove();
        }, 200);
      }, 2800);
    }

    #setDismissedCookie() {
      cookieApi.set(this.cookieName, 'dismissed', {
        path: '/',
        expires: this.cookieExpiresDays,
      });
    }

    #isDismissed() {
      return cookieApi.get(this.cookieName) === 'dismissed';
    }

    get delayMs() {
      const value = Number(this.getAttribute('popup-delay-ms'));

      return Number.isFinite(value) && value > 0 ? value : 4000;
    }

    get sectionId() {
      return this.getAttribute('popup-section-id') || '';
    }

    get cookieName() {
      return this.getAttribute('popup-cookie-name') || 'timedPopupDismissed';
    }

    get cookieExpiresDays() {
      const value = Number(this.getAttribute('popup-cookie-expires-days'));

      return Number.isFinite(value) && value > 0 ? value : 30;
    }

    get toastCookieName() {
      return `${this.cookieName}ToastSuccess`;
    }

    get successMessage() {
      return this.getAttribute('popup-success-message') || '';
    }

    get isDesignMode() {
      return Boolean(window.Shopify?.designMode);
    }
  }

  customElements.define('timed-popup-dialog', TimedPopupDialog);
}
